import React, { Component } from 'react';

import { Helmet } from 'react-helmet';
import get from 'lodash/get';

import PlacesPopup from './PlacesPopup';
import Input from '../../../components/UI/CustomInputField';
import Button from '../../../components/UI/Button/index.tsx';
import WidgetPropTypes from '../types';
import WidgetPageProps from '../../WidgetPage/types';
import { isPOBox } from '../../../helpers';
import addressValidation from '../../../services/addressValidation';
import VerifyAddressModal from './VerifyAddressModal';

export const WIDGET_NAME = 'set_legal_address';
export const STREET_ADDRESS_ERROR_MESSAGE =
  'Please enter a valid street address.';
export const CITY_ERROR_MESSAGE = 'Please enter a valid city.';
export const STATE_ERROR_MESSAGE = 'Please enter a valid state.';
export const ZIP_ERROR_MESSAGE = 'Please enter a valid zip code.';
export const PO_BOX_ERROR_MESSAGE = 'Address cannot be a PO Box';

class Address extends Component {
  constructor() {
    super();

    this.state = {
      address: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      poBox: '',
      poBoxChecked: false,
      error: false,
      addressError: false,
      cityError: false,
      stateError: false,
      zipError: false,
      editStreetAddress: false,
      displayVerifyAddress: false,
      suggestedAddress: {},
      confirmedAddress: false
    };
  }

  componentDidMount() {
    if (!this.props.legalAddress) {
      this.props.analytics.trackEvent('Registration Find Address Viewed');
    }

    if (get(this.props, 'app.prefilledAnswers.setLegalAddress')) {
      this.setState({
        address: this.props.app.prefilledAnswers.setLegalAddress.address,
        address2: this.props.app.prefilledAnswers.setLegalAddress.address2,
        city: this.props.app.prefilledAnswers.setLegalAddress.city,
        state: this.props.app.prefilledAnswers.setLegalAddress.state,
        zip: this.props.app.prefilledAnswers.setLegalAddress.zip
      });
    }
  }

  onChange = which => value => {
    this.setState({ [which]: value });
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      widget,
      legalAddress,
      shouldEnable,
      changeAnswerValue,
      submitAnswers,
      answerLegalAddress
    } = this.props;

    if (this.state !== prevState) {
      if (!legalAddress) {
        changeAnswerValue(widget, {
          address: this.state.address,
          address2: this.state.address2,
          city: this.state.city,
          state: this.state.state,
          zip: this.state.zip
        });
        if (this.state.address) {
          shouldEnable(widget, true);
        } else {
          shouldEnable(widget, false);
        }
      } else {
        changeAnswerValue({
          address: this.state.address,
          address2: this.state.address2,
          city: this.state.city,
          state: this.state.state,
          zip: this.state.zip
        });
      }

      if (this.state.confirmedAddress && legalAddress) {
        answerLegalAddress();
      } else if (this.state.confirmedAddress) {
        submitAnswers();
      }
    }
  }

  errorChecking = async () => {
    if (this.state.address.length < 2) {
      this.props.createError({
        message: STREET_ADDRESS_ERROR_MESSAGE,
        registrationStep: WIDGET_NAME
      });
      this.setState({ addressError: true });
      throw new Error();
    }
    if (this.state.city.length < 2) {
      this.props.createError({
        message: CITY_ERROR_MESSAGE,
        registrationStep: WIDGET_NAME
      });
      this.setState({ cityError: true });
      throw new Error();
    }
    if (this.state.state.length < 2) {
      this.props.createError({
        message: STATE_ERROR_MESSAGE,
        registrationStep: WIDGET_NAME
      });
      this.setState({ stateError: true });
      throw new Error();
    }
    if (this.state.zip.length < 5 || this.state.zip.length > 10) {
      this.props.createError({
        message: ZIP_ERROR_MESSAGE,
        registrationStep: WIDGET_NAME
      });
      this.setState({ zipError: true });
      throw new Error();
    }
    if (this.props.legalAddress && isPOBox(this.state.address)) {
      this.props.createError({
        message: PO_BOX_ERROR_MESSAGE,
        registrationStep: WIDGET_NAME
      });
      throw new Error();
    }
    if (
      this.props.toggles.uspsValidationUnblock.enabled &&
      !this.state.confirmedAddress
    ) {
      const validatedAddressResponse = await addressValidation.validateAddress({
        address: this.state
      });
      if (validatedAddressResponse.addressNotFound) {
        this.setState({
          displayVerifyAddress: true,
          suggestedAddress: null
        });
        throw new Error();
      } else if (
        validatedAddressResponse.address &&
        JSON.stringify(validatedAddressResponse).toLowerCase() !==
          JSON.stringify({
            address: this.state.address,
            address2: this.state.address2,
            city: this.state.city,
            state: this.state.state,
            zip: this.state.zip
          }).toLowerCase()
      ) {
        this.setState({
          displayVerifyAddress: true,
          suggestedAddress: validatedAddressResponse
        });
        throw new Error();
      }
    }
    return false;
  };

  submitLegalAddress = async () => {
    try {
      await this.errorChecking();
      if (this.props.answerLegalAddress) {
        this.props.answerLegalAddress();
      }
    } catch {
      window.scrollTo(0, 0);
    }
  };

  openStreetModal = () => {
    this.setState({ editStreetAddress: true });
  };

  handleAddress = e => {
    this.setState({
      editStreetAddress: true,
      address: e.target.value
    });
  };

  updateAddress = address => {
    this.setState({ address });
  };

  addressSelected = details => {
    const address = details.name; // "locality" : city
    let city = details.address_components.find(
      component => component.types.indexOf('locality') !== -1
    );
    if (!city)
      city = details.address_components.find(
        component =>
          component.types.indexOf('administrative_area_level_3') !== -1
      );
    const state = details.address_components.find(
      component => component.types.indexOf('administrative_area_level_1') !== -1
    );
    const zip = details.address_components.find(
      component => component.types.indexOf('postal_code') !== -1
    );
    this.setState({
      editStreetAddress: false,
      address,
      state: state ? state.short_name : '',
      city: city ? city.short_name : '',
      zip: zip ? zip.short_name : ''
    });
  };

  submitFromModal = ({ useSuggestedAddress }) => {
    const { widget, changeAnswerValue } = this.props;

    if (useSuggestedAddress) {
      this.setState({
        address: this.state.suggestedAddress.address,
        address2: this.state.suggestedAddress.address2,
        city: this.state.suggestedAddress.city,
        state: this.state.suggestedAddress.state,
        zip: this.state.suggestedAddress.zip,
        confirmedAddress: true
      });
      if (!this.props.legalAddress) {
        changeAnswerValue(widget, {
          address: this.state.suggestedAddress.address,
          address2: this.state.suggestedAddress.address2,
          city: this.state.suggestedAddress.city,
          state: this.state.suggestedAddress.state,
          zip: this.state.suggestedAddress.zip
        });
      } else {
        changeAnswerValue({
          address: this.state.suggestedAddress.address,
          address2: this.state.suggestedAddress.address2,
          city: this.state.suggestedAddress.city,
          state: this.state.suggestedAddress.state,
          zip: this.state.suggestedAddress.zip
        });
      }
    } else {
      this.setState({ confirmedAddress: true, displayVerifyAddress: false });
    }
  };

  render() {
    return (
      <div onClick={this.props.onClick}>
        {typeof window.google === 'undefined' && (
          <Helmet>
            <script src="https://maps.googleapis.com/maps/api/js?key=AIzaSyDQ9aXBTDi1Zx-ztQcPXTyCVwU0crdcC_c&libraries=places" />
          </Helmet>
        )}
        {this.state.displayVerifyAddress && (
          <VerifyAddressModal
            suggestedAddress={this.state.suggestedAddress}
            enteredAddress={this.state}
            submitAddress={this.submitFromModal}
            closeModal={() => this.setState({ displayVerifyAddress: false })}
            loading={this.props.app.loading}
          />
        )}
        <PlacesPopup
          open={this.state.editStreetAddress}
          onChange={this.handleAddress}
          onBlur={this.onBlur}
          address={this.state.address}
          onClick={this.openStreetModal}
          updateAddress={this.updateAddress}
          selectedAddress={this.addressSelected}
          data-e2e="places"
          error={this.state.addressError}
          initialValue={
            this.state.address ||
            get(this.props, 'app.prefilledAnswers.setLegalAddress.address')
          }
        />
        <Input
          id="address2Id"
          placeholder="Apartment or suite (optional)"
          value={this.state.address2}
          autoComplete="off"
          autoCorrect="off"
          onChange={this.onChange('address2')}
        />
        <Input
          id="cityId"
          placeholder="City"
          value={this.state.city}
          autoComplete="off"
          autoCorrect="off"
          onChange={this.onChange('city')}
          error={this.state.cityError}
        />
        <Input
          id="stateId"
          placeholder="State"
          value={this.state.state}
          autoComplete="off"
          autoCorrect="off"
          onChange={this.onChange('state')}
          error={this.state.stateError}
        />
        <Input
          id="zipId"
          placeholder="ZIP/Postal Code"
          value={this.state.zip}
          maxLength="5"
          autoComplete="off"
          autoCorrect="off"
          onChange={this.onChange('zip')}
          error={this.state.zipError}
        />
        {this.props.legalAddress && (
          <Button
            text="Next"
            loading={this.props.app.loading}
            onClick={this.submitLegalAddress}
            data-e2e="next"
          />
        )}
      </div>
    );
  }
}

Address.propTypes = {
  changeAnswerValue: WidgetPropTypes.changeAnswerValue.isRequired,
  index: WidgetPropTypes.index.isRequired,
  widget: WidgetPropTypes.widget.isRequired,
  createError: WidgetPageProps.createError
};

export default Address;
